import React from "react";
import "./BillingCardModal.css";

/**
 * Shared "Credit or Debit Card" modal.
 * When showCardFields=true (My Account): Card Number, Expiration Date, Security Code + name + billing.
 * When showCardFields=false (Checkout): billing only, note about card in next step.
 */
const BillingCardModal = ({
  isOpen,
  onClose,
  formData,
  onFormChange,
  onSubmit,
  errors = {},
  showCardFields = false,
}) => {
  if (!isOpen) return null;

  return (
    <div className="card-modal-overlay billing-card-modal-overlay" onClick={onClose}>
      <div
        className="card-modal-content billing-card-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-modal-header">
          <h2 className="card-modal-title">Credit or Debit Card</h2>
          <div className="card-modal-security">
            <span className="padlock-icon">🔒</span>
            <span className="security-text">Your payment is secure</span>
          </div>
          {!showCardFields && (
            <p className="card-modal-billing-note">
              Enter your billing details below. You will enter your card number, expiry, and CVC securely in the next step.
            </p>
          )}
        </div>

        <form onSubmit={onSubmit} className="card-modal-form">
          {showCardFields && (
            <div className="card-form-section">
              <div className="form-field">
                <label htmlFor="billing-cardNumber" className="form-label">
                  Card Number
                </label>
                <input
                  type="text"
                  id="billing-cardNumber"
                  name="cardNumber"
                  className={`form-input ${errors.cardNumber ? "form-input-error" : ""}`}
                  value={formData.cardNumber ?? ""}
                  onChange={onFormChange}
                  placeholder=""
                  maxLength={19}
                  required
                />
                {errors.cardNumber && (
                  <span className="form-error">
                    Error: {typeof errors.cardNumber === "string" ? errors.cardNumber : "Enter your card number."}
                  </span>
                )}
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="billing-expirationDate" className="form-label">
                    Expiration Date
                  </label>
                  <input
                    type="text"
                    id="billing-expirationDate"
                    name="expirationDate"
                    className="form-input"
                    value={formData.expirationDate ?? ""}
                    onChange={onFormChange}
                    placeholder="MM/YY"
                    maxLength={5}
                    required
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="billing-securityCode" className="form-label">
                    Security Code
                  </label>
                  <input
                    type="text"
                    id="billing-securityCode"
                    name="securityCode"
                    className="form-input"
                    value={formData.securityCode ?? ""}
                    onChange={onFormChange}
                    placeholder=""
                    maxLength={4}
                    required
                  />
                </div>
              </div>
            </div>
          )}

          <div className="card-form-section">
            <div className="form-row">
              <div className="form-field">
                <label htmlFor="billing-firstName" className="form-label">
                  First Name
                </label>
                <input
                  type="text"
                  id="billing-firstName"
                  name="firstName"
                  className="form-input"
                  value={formData.firstName ?? ""}
                  onChange={onFormChange}
                  required
                />
              </div>
              <div className="form-field">
                <label htmlFor="billing-lastName" className="form-label">
                  Last Name
                </label>
                <input
                  type="text"
                  id="billing-lastName"
                  name="lastName"
                  className="form-input"
                  value={formData.lastName ?? ""}
                  onChange={onFormChange}
                  required
                />
              </div>
            </div>

            <div className="form-checkbox">
              <input
                type="checkbox"
                id="billing-rememberCard"
                name="rememberCard"
                checked={!!formData.rememberCard}
                onChange={onFormChange}
              />
              <label htmlFor="billing-rememberCard" className="checkbox-label">
                Remember this card for future orders
              </label>
            </div>
          </div>

          <div className="card-form-section">
            <h3 className="form-section-title">Add Billing Address</h3>
            <div className="form-field">
              <label htmlFor="billing-country" className="form-label">
                Country or region
              </label>
              <select
                id="billing-country"
                name="billingCountry"
                className="form-input"
                value={formData.billingCountry ?? "United States"}
                onChange={onFormChange}
                required
              >
                <option value="United States">United States</option>
                <option value="Canada">Canada</option>
                <option value="United Kingdom">United Kingdom</option>
                <option value="Australia">Australia</option>
              </select>
            </div>

            <div className="form-row-two">
              <div className="form-field">
                <label htmlFor="billing-streetAddress" className="form-label">
                  Street Address
                </label>
                <input
                  type="text"
                  id="billing-streetAddress"
                  name="streetAddress"
                  className={`form-input ${errors.streetAddress ? "form-input-error" : ""}`}
                  value={formData.streetAddress ?? ""}
                  onChange={onFormChange}
                  required
                />
                {errors.streetAddress && (
                  <span className="form-error">
                    Error: {typeof errors.streetAddress === "string" ? errors.streetAddress : "Enter your street address"}
                  </span>
                )}
              </div>
              <div className="form-field">
                <label htmlFor="billing-streetAddress2" className="form-label">
                  Street Address 2 (optional)
                </label>
                <input
                  type="text"
                  id="billing-streetAddress2"
                  name="streetAddress2"
                  className="form-input"
                  value={formData.streetAddress2 ?? ""}
                  onChange={onFormChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="billing-city" className="form-label">
                  City
                </label>
                <input
                  type="text"
                  id="billing-city"
                  name="city"
                  className="form-input"
                  value={formData.city ?? ""}
                  onChange={onFormChange}
                  required
                />
              </div>
              <div className="form-field">
                <label htmlFor="billing-state" className="form-label">
                  State
                </label>
                <input
                  type="text"
                  id="billing-state"
                  name="state"
                  className="form-input"
                  value={formData.state ?? ""}
                  onChange={onFormChange}
                  required
                />
              </div>
              <div className="form-field">
                <label htmlFor="billing-zipCode" className="form-label">
                  Zip Code
                </label>
                <input
                  type="text"
                  id="billing-zipCode"
                  name="zipCode"
                  className="form-input"
                  value={formData.zipCode ?? ""}
                  onChange={onFormChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className="card-modal-buttons">
            <button
              type="button"
              className="card-modal-cancel-btn"
              onClick={onClose}
            >
              Cancel
            </button>
            <button type="submit" className="card-modal-add-btn">
              Add
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BillingCardModal;
